import { api, ApiError, type SectionDraftDetail, type SectionDraftStatus, type SocialPost } from '../api/client'
import type { Destination, StageKey } from './labels'
import type { Brief } from '../lib/brief'
import type { TopicRequest } from '../lib/sections'
import type { LiveReaders, PieceMetrics, Window } from './readers'
import type { ImportStatus } from './imports'

export type ChecklistItem = {
  key: string
  kind: 'unchecked' | 'search_title' | 'length' | 'member_link' | 'unsupported_share' | 'summary' | 'note'
  title: string
  detail: string
  claim: string | null
  blocking: boolean
}

/** DraftDetail as the E2 piece page sees it. */
export type PieceDetail = SectionDraftDetail & {
  updated_at: string
  checklist: ChecklistItem[]
  public_summary: string | null
  public_summary_edited: boolean
  /** A word about what a save did (a live summary that waits for the next sync). */
  note?: string | null
  /** A writer sent it to an editor (HANDOFF in labels.ts); by_id says whether it was me. */
  sent_to_editor_at?: string | null
  sent_to_editor_by_name?: string | null
  sent_to_editor_by_id?: number | null
  /** The last time an editor sent it back, while it is not sent again. */
  sent_back?: { at: string | null; by_name: string | null; note: string | null } | null
  /** The section brand the piece is filed under (Pit Lane brand pages). */
  brand?: BrandRef | null
  /** The piece's issue and which line of its Contents the piece is (that line links to the members' copy). */
  contents_line?: { edition_title: string; toc_id: number | null; lines: { id: number; page: number | null; title: string }[] } | null
  /** A save took a typed "See more posts about …" line out of the body (never stored). */
  closing_line_notice?: string | null
  /** An edit draft: the live article it saves over (save-in-place), else null. */
  edits_article_id?: number | null
  /** An edit draft opened before its article last changed: it can't be saved or edited. */
  edit_stale?: boolean
  /** An edit draft's locked blocks, by key: each one's plain text (server-made; shown as text only). */
  locked_previews?: Record<string, string>
  /** A refresh draft (Improve this page): its web-only facts to tick off and the searches it targets. */
  refresh?: RefreshInfo | null
  /** An edit draft whose article has a live copy on the other site: the copy this edit leaves as it is. */
  edit_twin_site?: 'members' | 'free' | null
  /** The featured photo's framing. Coordinates are fractions of the photo SHOWN (the trim, when
   *  there is one), unlike the framing routes' FramingOut, which is in fractions of the root original. */
  featured_framing: {
    focal_x: number | null
    focal_y: number | null
    fit: Record<string, 'cover' | 'contain'> | null
    subject_box: [number, number, number, number] | null
    source: 'piece' | 'suggestion' | 'none'
    width: number | null
    height: number | null
    trimmed: boolean
  } | null
}

/** What the signed-in person may do in Editorial Desk (GET /api/editorial/me). */
export type EditorialMe = { user_id: number; role: string; can_publish: boolean; is_admin: boolean; titles: string[] }
/** A piece a writer sent to an editor, on the Desk. */
export type SentRow = { id: number; title: string; sent_at: string; by_name: string | null }

export type DeskAction = 'review' | 'check_sources' | 'try_again' | 'publish_public_copy'
export type DeskRow = {
  id: number
  title: string
  status: SectionDraftStatus
  stage: StageKey | null
  destination: Destination
  format: string
  reason: string
  action: DeskAction
}
/** The running job (the section agent's in-memory status). */
export type DeskWriting = {
  running: true
  stage: string | null
  topic_label: string | null
  started_at: string | null
  requested_by: string | null
  format: string
  destination: Destination
  estimate_minutes: number
  run_id?: number
  /** Pieces already saved while the run goes on (a versions run adds each as it lands). */
  result_draft_ids?: number[]
  [k: string]: unknown
}
/** A finished run still on the Desk: pieces nobody has opened yet, or a failure until dismissed. */
export type DeskFinishedRun = {
  run_id: number
  status: 'done' | 'failed' | 'stopped'
  kind: 'draft' | 'job' | 'write' | 'revise' | 'recheck' | 'refresh'
  topic_label: string | null
  error: string | null
  finished_at: string | null
  pieces: { id: number; title: string; status: SectionDraftStatus }[]
}
export type DeskSlot = { at: string; draft_id: number | null; title: string | null; destination: Destination | null }
export type DeskIdea = { label: string; ask: string; reasons: string[]; strength: number }
export type DeskResponse = {
  counts: { writing: number; check_sources: number; to_review: number; needs_a_look: number; approved?: number; scheduled: number; live_this_month: number }
  needs_you: DeskRow[]
  /** Everything that needs you; needs_you holds only the first few. */
  needs_count: number
  writing: DeskWriting | null
  finished_runs?: DeskFinishedRun[]
  next_slots: DeskSlot[]
  ideas: DeskIdea[]
  /** The ideas could not be worked out just now. */
  ideas_error: boolean
  releases_on: boolean
  versions_on: boolean
  imports_on?: boolean
  photo_help_on?: boolean
  search_ideas_on?: boolean
  page_refresh_on?: boolean
  /** Pit Lane brand pages are on for the title: the piece page shows the brand picker. */
  section_brands_on?: boolean
  /** Top pieces published in the last 30 days, by readers. */
  recent: DeskRecent[]
  /** Every feedback message for the title. */
  feedback_total: number
  tracking_since: string | null
  /** Editors: every piece a writer sent. Writers: their own, waiting for an editor. Newest first. */
  sent_to_editor?: SentRow[]
}
export type DeskRecent = {
  id: number; title: string; destination: Destination; published_at: string
  people: number; finished_pct: number | null; members_pct: number; signups_after: number
  members_gained?: number; source?: 'desk' | 'raw'
  /** "Tell us straight" messages sent from this piece's pages. */
  feedback: number
}

export type FeedbackKind = 'good' | 'bad' | 'ugly'
export type DeskFeedbackItem = {
  id: number; kind: FeedbackKind; message: string; created_at: string | null
  audience: 'member' | 'visitor'; page_label: string; page_url: string | null
  piece_id: number | null; piece_title: string | null
  /** Editors only; null for a writer. */
  email: string | null; member_name: string | null
  /** The conversation (editors only; empty for a writer). */
  replies: FeedbackReply[]; answers: FeedbackAnswer[]
  /** A draft with open account or billing actions: Customer Service answers this one. */
  support_hold: boolean
  can_reply: boolean
  /** The Desk's own archive, shared by the title's editors. Set with archived false: the reader wrote back since. */
  archived_at: string | null; archived_by: string | null; archived: boolean
}
export type FeedbackView = 'active' | 'archived'
export type FeedbackReply = { subject: string; body_md: string; sent_by: string; created_at: string | null }
export type FeedbackAnswer = { from_name: string | null; body_text: string | null; received_at: string | null; auto_reply: boolean }
export type DeskFeedback = {
  items: DeskFeedbackItem[]; counts: Record<FeedbackKind, number>; total: number; truncated: boolean
  view: FeedbackView; views: Record<FeedbackView, number>
  piece: { id: number; title: string | null } | null
}

export type PieceRow = {
  id: number
  title: string
  subject: string | null
  status: SectionDraftStatus
  stage: StageKey | null
  destination: Destination
  format: string
  word_count: number
  updated_at: string | null
  requested_by: string | null
  revised_from_id: number | null
  revision_note: string | null
  open_items: number
  error: string | null
  published_at: string | null
  sent_to_editor_at?: string | null
  source_changed?: boolean
}
export type PieceList = { items: PieceRow[]; total: number; counts: Record<StageKey, number>; sent_to_editor?: number }
export type ListPiecesParams = { stage?: StageKey | null; q?: string; destination?: Destination | null; sent?: boolean; limit?: number; offset?: number }

export type PatchBody = {
  title?: string
  seo_title?: string
  meta_description?: string
  slug?: string
  body_html?: string
  featured_caption_override?: string
  byline?: string
  review_note?: string
  destination?: Destination
  public_summary?: string
  /** The section brand (Pit Lane brand pages); null clears it. Saved by the brand picker, not the autosave. */
  brand_id?: number | null
  base_updated_at?: string
}

export type PublishPreview = {
  destination: Destination
  where: { audience: string; label: string; detail: string }[]
  checklist: ChecklistItem[]
  next_slot: string | null
  releases_on: boolean
  queue_length: number
}
export type PublishBody = { when: 'now' | 'next_slot'; acknowledged: boolean }
/** "now" answers with the detail; "next_slot" wraps it with the slot. */
export type PublishResult = PieceDetail | { draft: PieceDetail; slot_at: string | null }

const SD = '/api/lab/section-drafts'
const ED = '/api/editorial'

function qs(params: Record<string, string | number | null | undefined>): string {
  const u = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v))
  return u.toString()
}

export const getPieceMetrics = (id: number, days: Window) => api.get<PieceMetrics>(`${ED}/pieces/${id}/metrics?${qs({ days })}`)
export const getDesk = (slug: string) => api.get<DeskResponse>(`${ED}/desk?${qs({ publication_slug: slug })}`)
/** The piece page was opened: the piece leaves its run's Desk card. */
export const markPieceOpened = (id: number) => api.post<unknown>(`${ED}/pieces/${id}/opened`, {})
/** Take a failed or stopped run off the Desk. */
export const dismissRun = (slug: string, runId: number) =>
  api.post<unknown>(`${ED}/runs/${runId}/dismiss?${qs({ publication_slug: slug })}`, {})
export const getDeskFeedback = (slug: string, pieceId: number | null = null, view: FeedbackView = 'active') =>
  api.get<DeskFeedback>(`${ED}/feedback?${qs({ publication_slug: slug, piece_id: pieceId, view })}`)
/** Off (or back on) the Desk's Active list for every editor of the title; the Feedback inbox is untouched. */
export const archiveFeedback = (slug: string, id: number) => api.post<{ id: number }>(`${ED}/feedback/${id}/archive?${qs({ publication_slug: slug })}`)
export const restoreFeedback = (slug: string, id: number) => api.post<{ id: number }>(`${ED}/feedback/${id}/restore?${qs({ publication_slug: slug })}`)
/** An editor answers a reader, through the Feedback inbox's own send (title From address and sign-off). */
export const replyToFeedback = (slug: string, id: number, body: { subject: string; body_md: string; confirm_repeat?: boolean }) =>
  api.post<FeedbackReply>(`${ED}/feedback/${id}/reply?${qs({ publication_slug: slug })}`, body)
export const listPieces = (slug: string, p: ListPiecesParams = {}) =>
  api.get<PieceList>(`${ED}/pieces?${qs({ publication_slug: slug, stage: p.stage, q: p.q, destination: p.destination, sent: p.sent ? 'true' : null, limit: p.limit, offset: p.offset })}`)
export const getEditorialMe = () => api.get<EditorialMe>(`${ED}/me`)
/** Send to editor (writer or editor). */
export const sendToEditor = (id: number) => api.post<PieceDetail>(`${ED}/pieces/${id}/send-to-editor`)
/** Take back (the writer who sent it) or Send back to writer (an editor, with an optional note). */
export const takeBackFromEditor = (id: number, note?: string | null) =>
  api.del<PieceDetail>(`${ED}/pieces/${id}/send-to-editor`, note ? { note } : undefined)
/** Approve a piece (editor). 409 `blocking_items` while checklist items are open and not acknowledged. */
export const approvePiece = (id: number, acknowledged = false) =>
  api.post<PieceDetail>(`${ED}/pieces/${id}/approve`, { acknowledged })
/** Take the approval back: the piece returns to review. */
export const unapprovePiece = (id: number) => api.del<PieceDetail>(`${ED}/pieces/${id}/approve`)
export const getPiece = (id: number) => api.get<PieceDetail>(`${SD}/${id}`)
export const putContentsLine = (id: number, tocId: number | null) =>
  api.put<PieceDetail>(`${SD}/${id}/contents-line`, { toc_id: tocId })
export const patchPiece = (id: number, body: PatchBody) => api.patch<PieceDetail>(`${SD}/${id}`, body)

/** A section brand (Pit Lane brand pages). */
export type BrandRef = { id: number; slug: string; name: string }
/** A brand in the picker: live_count is its live free pieces. */
export type BrandRow = BrandRef & { live_count: number }
export const listBrands = (pub: string) => api.get<BrandRow[]>(`${ED}/brands?${qs({ pub })}`)
/** Add a brand; one the title already has (same address) comes back as it is. */
export const createBrand = (pub: string, name: string) => api.post<BrandRow>(`${ED}/brands`, { pub, name })
/** Rename a brand (editors); a new address leaves a 301 from the old one. */
export const renameBrand = (id: number, name: string) => api.patch<BrandRow>(`${ED}/brands/${id}`, { name })
/** Fold a brand into another of the same title (editors): pieces move, the old address redirects. */
export const mergeBrand = (id: number, into_id: number) => api.post<BrandRow>(`${ED}/brands/${id}/merge`, { into_id })
/** A manufacturer website whose product photos the drafting agent may use (all titles). */
export type PhotoSite = { id: number; domain: string; brand: string; added_by: string | null }
export const listPhotoSites = () => api.get<PhotoSite[]>(`${ED}/photo-sites`)
/** Administrators only. */
export const addPhotoSite = (domain: string, brand: string) => api.post<PhotoSite>(`${ED}/photo-sites`, { domain, brand })
export const removePhotoSite = (id: number) => api.del<{ ok: boolean }>(`${ED}/photo-sites/${id}`)
/** A staff email list on Settings: the weekly package Heads Up list (all titles) or a title's Readers Rides list.
 *  source: 'list' = edited here; 'server' = still the server setting (the first change copies it in); 'none' = no one. */
export type StaffEmails = { emails: string[]; source: 'list' | 'server' | 'none' }
export const staffEmailsPath = (which: 'weekly-package' | { readersRides: string }) =>
  `${ED}/staff-emails/${which === 'weekly-package' ? which : `readers-rides/${encodeURIComponent(which.readersRides)}`}`
export const getStaffEmails = (path: string) => api.get<StaffEmails>(path)
/** Administrators only. */
export const addStaffEmail = (path: string, email: string) => api.post<StaffEmails>(path, { email })
export const replaceStaffEmail = (path: string, old: string, email: string) => api.patch<StaffEmails>(path, { old, email })
export const removeStaffEmail = (path: string, email: string) => api.del<StaffEmails>(`${path}?email=${encodeURIComponent(email)}`)
export const confirmClaim = (id: number, text: string) => api.post<PieceDetail>(`${SD}/${id}/claims/confirm`, { text })
export const removeClaim = (id: number, text: string) => api.post<PieceDetail>(`${SD}/${id}/claims/remove`, { text })
export const putBackUp = (id: number) => api.post<PieceDetail>(`${ED}/pieces/${id}/put-back-up`)
export const recheck = (id: number) => api.post<unknown>(`${SD}/${id}/recheck`)
export const generateSummary = (id: number) => api.post<PieceDetail>(`${SD}/${id}/summary/generate`)
export const publishPreview = (id: number) => api.get<PublishPreview>(`${ED}/pieces/${id}/publish-preview`)
export const publish = (id: number, body: PublishBody) => api.post<PublishResult>(`${ED}/pieces/${id}/publish`, body)

// --- Editing a live article (native titles only: LiveList.can_edit) ---
/** Open (or reopen) the article's edit draft. edit_stale: it was opened before the article last changed. */
export const openEdit = (articleId: number) => api.post<{ draft_id: number; edit_stale: boolean }>(`${ED}/articles/${articleId}/edit`)
/** Start the edit over from the live article: the old draft is set aside but stays readable. */
export const restartEdit = (articleId: number) =>
  api.post<{ draft_id: number; rejected_draft_id: number | null }>(`${ED}/articles/${articleId}/edit/restart`)
/** Save and publish: the edit draft over its live article. 409 would_lose lists what it would drop
 *  (saveInPlace.ts); resend with those keys in `acknowledged` to save anyway. */
export const saveInPlace = (id: number, acknowledged: string[] = []) =>
  api.post<{ url: string | null; draft: PieceDetail }>(`${ED}/pieces/${id}/save-in-place`, { acknowledged })

/** The machine code of a refusal whose `detail` is {code, message}
 *  ("stale", "unchecked", "releases_off"), or null for any other error. */
export function errorCode(err: unknown): string | null {
  if (!(err instanceof ApiError) || typeof err.detail !== 'object' || err.detail === null) return null
  const d = (err.detail as { detail?: unknown }).detail
  if (d && typeof d === 'object' && typeof (d as { code?: unknown }).code === 'string') return (d as { code: string }).code
  return null
}

/** Start a piece. 202 {started}; a 409 means a draft is already being written. */
/** 202 either way: `started: false, already_running: true` means another piece is being written for the title. */
export type StartResult = { started: boolean; already_running?: boolean; status?: DeskWriting }
export const draftNow = (slug: string, body: { topic: TopicRequest; brief: Brief }) =>
  api.post<StartResult>(`${SD}/draft-now?${qs({ publication_slug: slug })}`, body)
export const getMyDefaults = (slug: string) => api.get<Brief>(`${SD}/my-defaults?${qs({ publication_slug: slug })}`)
export const putMyDefaults = (slug: string, body: Pick<Brief, 'format' | 'sources' | 'destination'>) =>
  api.put<unknown>(`${SD}/my-defaults?${qs({ publication_slug: slug })}`, body)
/** The piece "Draft this subject again" starts from: its topic and brief. */
export const getPieceForRedraft = getPiece

/** Remove sources by id and include archive refs, on a piece waiting in sources_ready. */
export const patchSources = (id: number, body: { remove: string[]; include: { kind: 'member' | 'issue'; id: number }[] }) =>
  api.patch<unknown>(`${SD}/${id}/sources`, body)
/** Write a piece from its checked sources. 202; a 409 means one is already being written, or this one is not waiting. */
export const writeIt = (id: number) => api.post<StartResult>(`${SD}/${id}/write`)
/** Set a piece aside, which frees its subject for a new piece. */
export const setAside = (id: number) => api.post<unknown>(`${SD}/${id}/reject`, {})

export type IdeaReason = { key: string; label: string; evidence: string | null; url: string | null }
export type IdeaPiece = { id: number; status: SectionDraftStatus; stage: StageKey | null }
export type Idea = {
  rank: number
  label: string
  ask: string
  piece_type: string | null
  reasons: IdeaReason[]
  strength: number
  strength_label: string
  archive: string
  /** The latest piece on this subject, whatever its state. */
  piece: IdeaPiece | null
  /** True only while that piece is live (draft, needs a look, sources ready, queued or published). */
  written: boolean
  /** The whole ranked subject, sent to draft-now as it is. */
  topic: TopicRequest
}
export type IdeasResponse = {
  items: Idea[]
  computed_at?: string
  counts?: Record<'all' | 'searched' | 'trending' | 'anniversaries' | 'news' | 'unwritten', number>
  error?: string
}
export const getIdeas = (slug: string, show: string) => api.get<IdeasResponse>(`${ED}/ideas?${qs({ publication_slug: slug, show })}`)
export const refreshIdeas = (slug: string) =>
  api.post<{ started: boolean; already_running: boolean }>(`${ED}/ideas/refresh?${qs({ publication_slug: slug })}`)

// --- Schedule and Settings (E4) ---
export type ScheduleSlot = { at: string; piece: { id: number; title: string; destination: Destination; length: number } | null }
export type ScheduleDay = { date: string; slots: ScheduleSlot[] }
export type ScheduleItem = { id: number; title: string; destination: Destination; format: string; slot_at: string | null; no_credit: boolean }
export type ScheduleResponse = { releases_on: boolean; slots_text: string | null; days: ScheduleDay[]; queue: ScheduleItem[] }
export const getSchedule = (slug: string) => api.get<ScheduleResponse>(`${ED}/schedule?${qs({ publication_slug: slug })}`)
export const moveScheduled = (slug: string, id: number, direction: 'up' | 'down') =>
  api.post<{ order: number[] }>(`${ED}/schedule/move?${qs({ publication_slug: slug })}`, { id, direction })
export const unschedule = (id: number) => api.post<unknown>(`${ED}/schedule/${id}/remove`)

/** Days are 0 = Monday … 6 = Sunday; release_time is "HH:MM" in `tz`. */
export type Slots = { days_of_week: number[]; release_time: string; tz: string; release_time_utc_today: string }
export type PublishTarget = 'wordpress' | 'native'
export type SwitchSource = 'setting' | 'server' | string
export type TitleSettings = {
  slots: Slots | null
  releases_on: boolean
  releases_source: SwitchSource
  scheduled_writing_on: boolean
  scheduled_writing_source: SwitchSource
  versions_on: boolean
  imports_on?: boolean
  photo_help_on?: boolean
  search_ideas_on?: boolean
  page_refresh_on?: boolean
  /** Pit Lane brand pages are on for the title: the piece page shows the brand picker. */
  section_brands_on?: boolean
  /** The weekly package switches (admins change them). */
  weekly_package_on?: boolean
  weekly_package_live?: boolean
  /** From WordPress, automatic: the title's WordPress posts are imported, approved and published by themselves. */
  wp_auto_publish_on?: boolean
  publish_target?: PublishTarget
  scheduled_brief: { brief: Brief; updated_by: string | null; updated_at: string | null }
  my_defaults: Brief
  can_admin: boolean
}
export type SwitchesBody = { releases_on?: boolean | null; scheduled_writing_on?: boolean | null; versions_on?: boolean | null; imports_on?: boolean | null; photo_help_on?: boolean | null; search_ideas_on?: boolean | null; page_refresh_on?: boolean | null; weekly_package_on?: boolean | null; weekly_package_live?: boolean | null; wp_auto_publish_on?: boolean | null }
export const getTitleSettings = (slug: string) => api.get<TitleSettings>(`${ED}/settings?${qs({ publication_slug: slug })}`)
export const putSlots = (slug: string, body: { days_of_week: number[]; release_time: string }) =>
  api.put<Slots | null>(`${ED}/settings/slots?${qs({ publication_slug: slug })}`, body)
export const putSwitches = (slug: string, body: SwitchesBody) =>
  api.put<Pick<TitleSettings, 'releases_on' | 'releases_source' | 'scheduled_writing_on' | 'scheduled_writing_source'>>(`${ED}/settings/switches?${qs({ publication_slug: slug })}`, body)
export const putPublishTarget = (slug: string, publish_target: PublishTarget) =>
  api.put<{ publish_target: PublishTarget }>(`${ED}/settings/publish-target?${qs({ publication_slug: slug })}`, { publish_target })
export const putScheduledBrief = (slug: string, brief: Brief) => api.put<unknown>(`${ED}/settings/scheduled-brief?${qs({ publication_slug: slug })}`, brief)
export const deleteScheduledBrief = (slug: string) => api.del<unknown>(`${ED}/settings/scheduled-brief?${qs({ publication_slug: slug })}`)
export const putEditorialDefaults = (body: Pick<Brief, 'format' | 'sources' | 'destination'>) => api.put<unknown>(`${ED}/my-defaults`, body)
export type Person = { name: string; role: string; titles: string[]; email?: string }
export const getPeople = (slug: string) => api.get<Person[]>(`${ED}/people?${qs({ publication_slug: slug })}`)

// --- Live ---
export type LiveWhere = 'free' | 'members' | 'taken_down'
export type LiveCopy = 'free' | 'members'
export type TakeDownReason = 'member_archive' | 'out_of_date' | 'unavailable' | 'custom'
export type LiveItem = {
  article_id: number
  title: string
  came_from: 'here' | 'new_site' | 'wordpress'
  draft_id: number | null
  live_since: string | null
  /** The piece's other copy, and whether it is down right now. */
  twin: { article_id: number; where: LiveCopy; taken_down: boolean } | null
  taken_down: { reason: string | null; by: string | null; at: string | null } | null
  where: LiveCopy
  slug: string | null
  copies: LiveCopy[]
  /** Every copy of the piece that is down now (this row's and its twin's). */
  down: LiveCopy[]
  /** The reader page, built by the server (next.* for a free copy, the public host for members); null while down. */
  url: string | null
  readers: LiveReaders
}
export type LiveList = { items: LiveItem[]; total: number; can_edit?: boolean; counts: Record<LiveWhere, number>; tracking_since?: string | null }
export type TakeDownBody = { copies: LiveCopy[]; reason: TakeDownReason; custom_text?: string; note?: string }
export type LiveResult = { item: LiveItem; wordpress: Record<string, string>; draft_status: string | null }
export type MessagePreview = { reason: string; message: string; member_twin: { article_id: number; title: string; issue_label: string | null } | null }

export const listLive = (slug: string, p: { where: LiveWhere; q?: string; limit?: number; offset?: number }) =>
  api.get<LiveList>(`${ED}/live?${qs({ publication_slug: slug, where: p.where, q: p.q, limit: p.limit, offset: p.offset })}`)
export const takeDown = (id: number, body: TakeDownBody) => api.post<LiveResult>(`${ED}/live/${id}/take-down`, body)
export const putLiveBackUp = (id: number, copies: LiveCopy[]) => api.post<LiveResult>(`${ED}/live/${id}/put-back-up`, { copies })
export const messagePreview = (id: number, reason: TakeDownReason, customText?: string) =>
  api.get<MessagePreview>(`${ED}/live/${id}/message-preview?${qs({ reason, custom_text: reason === 'custom' ? customText : undefined })}`)

// --- Social and Topic pages (E4) ---
export type SocialReview = {
  posting_on: boolean
  accounts: { platform: string; connected: boolean }[]
  pieces: { id: number; title: string | null; piece_status: string; live_since: string | null; posts: SocialPost[] }[]
  counts: Record<'to_approve' | 'waiting' | 'posted' | 'needs_checking', number>
}
export const getSocial = (slug: string, show: string) => api.get<SocialReview>(`${ED}/social?${qs({ publication_slug: slug, show })}`)
export const socialPost = (draftId: number, platform: string, verb: 'rewrite' | 'approve' | 'reject' | 'publish') =>
  api.post<unknown>(`${SD}/${draftId}/social/${platform}/${verb}`, {})
export const socialSave = (draftId: number, platform: string, body: string) =>
  api.patch<unknown>(`${SD}/${draftId}/social/${platform}`, { body })
export const socialPublishAll = (draftId: number) => api.post<unknown>(`${SD}/${draftId}/social/publish-all`, {})

export type TopicPageRow = {
  id: number
  name: string
  slug: string
  state: 'proposed' | 'hidden' | 'live' | 'retired' | string
  article_count: number
  updated_at: string | null
  live_url: string | null
  /** Proposals only: a page already lives at this address, so it can only be dismissed. */
  slug_taken?: boolean
}
/** One page of the list (50 by default): live, hidden, proposed, retired. */
export type TopicPageList = { hidden_since: string | null; pages: TopicPageRow[]; total: number; next_offset: number | null; proposed: number }
export const getTopicPages = (slug: string, offset?: number) =>
  api.get<TopicPageList>(`${ED}/topic-pages?${qs({ publication_slug: slug, offset })}`)
export const topicPageAct = (path: string) => api.post<unknown>(path, {})

export type MakeVersionsBody = {
  source: { draft_id?: number; article_id?: number }
  versions: { format: 'short' | 'long' | 'deep'; destination: 'section' | 'membership' | 'both' }[]
  start: 'ai' | 'blank'
  note: string
}
export type MakeVersionsResult = { started: boolean; already_running?: boolean; draft_ids?: number[]; status?: DeskWriting }
export type VersionRow = { id: number; title: string; format: string; destination: string; status: string; source_changed: boolean }
export type SourcePhoto = { id: number; url: string; caption: string | null; width: number | null; height: number | null }
export type VersionFamily = {
  source: { kind: 'draft' | 'article'; id: number; title: string } | null
  versions: VersionRow[]
  source_text: string | null
  source_photos: SourcePhoto[]
}
export type SourceChanges = { ops: { op: 'equal' | 'insert' | 'delete'; text: string }[]; changed_at: string | null }

export const makeVersions = (slug: string, body: MakeVersionsBody) =>
  api.post<MakeVersionsResult>(`${ED}/versions?${qs({ publication_slug: slug })}`, body)
/** What the Make a version form needs about its source; any editor of the title may read it. */
export type VersionSourceInfo = { title: string; format: 'short' | 'long' | 'deep'; kind: 'draft' | 'member' | 'public' }
export const getVersionSource = (slug: string, src: { draft_id?: number; article_id?: number }) =>
  api.get<VersionSourceInfo>(`${ED}/version-source?${qs({ publication_slug: slug, ...src })}`)
export const pieceVersions = (id: number) => api.get<VersionFamily>(`${ED}/pieces/${id}/versions`)
export const sourceChanges = (id: number) => api.get<SourceChanges>(`${SD}/${id}/source-changes`)
export const markSourceReviewed = (id: number) => api.post<PieceDetail>(`${SD}/${id}/source-reviewed`)

// ---- Import (spec 2026-10-15 §5) ----
export type ImportFile = { index: number; name: string; size: number; kind: string; status: string; error: string | null }
export type ImportPiece = {
  key: string; file: number; start: number; end: number; title: string; byline: string | null; deck: string | null
  images: number[]; hero: number | null; dropped: boolean; warnings: string[]
  already_published: { title: string; where: string; id: number } | null
  draft_id: number | null; error: string | null
  first_lines: string; headings: { index: number; text: string }[]
  image_list: { index: number; thumb: string; caption: string | null }[]; words: number
}
export type ImportDetail = {
  id: number; publication_slug: string; status: ImportStatus; created_by: string | null; created_at: string
  digital_edition_id: number | null; files: ImportFile[]; pieces: ImportPiece[]; warnings: string[]; error: string | null
}
export type ImportRow = {
  id: number; created_at: string; created_by: string | null; status: ImportStatus
  files: { name: string; status: string }[]; pieces_total: number; drafts: number
}
export type EditionOption = { id: number; label: string }
export const createImport = (slug: string, editionId: number | null) =>
  api.post<{ id: number }>(`${ED}/imports?${qs({ publication_slug: slug })}`, { digital_edition_id: editionId })
export const listImports = (slug: string) => api.get<{ items: ImportRow[] }>(`${ED}/imports?${qs({ publication_slug: slug })}`)
export const getImport = (id: number) => api.get<ImportDetail>(`${ED}/imports/${id}`)
/** Start over: only an import that was never started can go. */
export const deleteImport = (id: number) => api.del<unknown>(`${ED}/imports/${id}`)
export const removeImportFile = (id: number, index: number) => api.del<unknown>(`${ED}/imports/${id}/files/${index}`)
export const startImport = (id: number) => api.post<{ status: string }>(`${ED}/imports/${id}/start`)
export const putImportPieces = (id: number, pieces: unknown[]) => api.put<ImportDetail>(`${ED}/imports/${id}/pieces`, { pieces })
export const confirmImport = (id: number) => api.post<unknown>(`${ED}/imports/${id}/confirm`)
export const retryImportPiece = (id: number, key: string) => api.post<unknown>(`${ED}/imports/${id}/pieces/${encodeURIComponent(key)}/retry`)
export const importFileUrl = (id: number) => `${ED}/imports/${id}/files`
export const listEditions = (slug: string) => api.get<EditionOption[]>(`${ED}/editions?${qs({ publication_slug: slug })}`)

// --- Weekly members' package ---
export type PackageStatus = 'planned' | 'complete' | 'sending' | 'held' | 'sent' | 'staff_sent' | 'not_sent' | 'cancelled'
/** `gaps`: what the week's email needs from this piece and it lacks (the server's words). */
export type PackageDraft = { id: number; title: string | null; status: string | null; gaps?: string[] }
export type WeeklyPackage = {
  id: number
  publication_slug: string
  send_at: string
  status: PackageStatus
  /** The live answer: "complete" when both pieces are approved right now. */
  state?: 'complete' | 'incomplete'
  /** Plain sentences from the server, shown as given. */
  reasons: string[]
  hold_reason: string | null
  full: PackageDraft
  short: PackageDraft
  members_campaign_id: number | null
  join_campaign_id: number | null
  published_at: string | null
  released_by: string | null
}
export type PackageSlot = { send_at: string; package: WeeklyPackage | null }
/** A piece already in one of the title's packages: `went` when that package went out (or is going). */
export type UsedPiece = { draft_id: number; package_id: number; send_at: string; went: boolean }
export type PackageList = { publication: string; weekly_package_on: boolean; weekly_package_live: boolean; upcoming: PackageSlot[]; sent: WeeklyPackage[]; used?: UsedPiece[] }
export type CampaignNumbers = { sent: number; opens: number; clicks: number }
export type PackageHistoryRow = {
  id: number; send_at: string; status: PackageStatus; full_title: string | null; short_title: string | null
  members: CampaignNumbers; join: CampaignNumbers & { joins: number }
}
const PK = `${ED}/packages`
export const listPackages = (slug: string) => api.get<PackageList>(`${PK}?${qs({ publication: slug })}`)
export const packageHistory = (slug: string, limit = 12) =>
  api.get<{ publication: string; history: PackageHistoryRow[] }>(`${PK}/history?${qs({ publication: slug, limit })}`)
export type PackagePreviewEmail = {
  key: 'members' | 'join_expired' | 'join_signup'; label: string; audience: string; count: number | null
  from?: string; subject: string; preheader: string; html: string; text: string; error: string | null
}
/** A link in the emails to a piece not on the site yet: the send publishes it first, so until then it opens a missing page. */
export type PackageNotLiveLink = { role: 'full' | 'short'; label: string; url: string }
export type PackagePreview = { send_at: string; draft: boolean; problems: string[]; emails: PackagePreviewEmail[]; not_live: PackageNotLiveLink[] }
export const previewPackage = (slug: string, sendAt: string, fullId: number, shortId: number) =>
  api.get<PackagePreview>(`${PK}/preview?${qs({ publication: slug, send_at: sendAt, full_draft_id: fullId, short_draft_id: shortId })}`)
export const packageGaps = (draftId: number, role: 'full' | 'short') =>
  api.get<{ draft_id: number; role: string; gaps: string[] }>(`${PK}/gaps?${qs({ draft_id: draftId, role })}`)
export const createPackage = (body: { publication_slug: string; send_at: string; full_draft_id: number; short_draft_id: number; extra?: boolean }) =>
  api.post<WeeklyPackage>(PK, body)
export const updatePackage = (id: number, body: { full_draft_id?: number; short_draft_id?: number }) =>
  api.put<WeeklyPackage>(`${PK}/${id}`, body)
export const cancelPackage = (id: number) => api.del<WeeklyPackage>(`${PK}/${id}`)
export const releasePackage = (id: number) => api.post<WeeklyPackage>(`${PK}/${id}/release`)

export type SearchTotals = { published: number; counted: number; clicks: number; impressions: number }
export type SearchPiece = {
  draft_id: number; title: string; published_at: string | null; phrase: string | null
  counted: boolean; free_version?: boolean; clicks: number; impressions: number; avg_position: number | null; url: string | null
}
export type SearchResults = { window_days: number; search: SearchTotals; others: SearchTotals; pieces: SearchPiece[] }
export const getSearchResults = (slug: string, days = 28) =>
  api.get<SearchResults>(`${ED}/ideas/search-results?${qs({ publication_slug: slug, days })}`)
export const notOurHobby = (slug: string, label: string) =>
  api.post<{ blocked: string[]; card_removed: boolean }>(`${ED}/ideas/not-our-hobby?${qs({ publication_slug: slug })}`, { label })
export type SearchBlock = { keyword: string; created_by: string | null; created_at: string | null }
export const getSearchBlocks = (slug: string) =>
  api.get<{ items: SearchBlock[] }>(`${ED}/settings/search-blocks?${qs({ publication_slug: slug })}`)
export const removeSearchBlock = (slug: string, keyword: string) =>
  api.del<{ removed: boolean }>(`${ED}/settings/search-blocks?${qs({ publication_slug: slug, keyword })}`)

// --- Improve this page ---
/** A fact to tick off before Save and publish. ``kind`` "unlocated": a claim the fact check found no support
 *  for but could not find in the text to cut (no urls). No kind: only web pages back it. */
export type WebPassage = { key: string; text: string; urls: string[]; kind?: 'web' | 'unlocated'; source_ids?: string[]; checked_by?: string | null; checked_at?: string | null }
/** ``cut``: how many claims the fact check cut from the draft. */
export type RefreshInfo = { web_passages: WebPassage[]; queries: { query: string; impressions: number; position: number | null }[]; cut?: number }
export type RefreshCard = {
  article_id: number; title: string; page_url: string; position: number; impressions: number; clicks: number; upside: number
  queries: { query: string; impressions: number; position: number | null }[]
}
export type RefreshNumbers = { position: number | null; clicks: number; impressions: number }
export type RefreshResult = {
  article_id: number; title: string; page_url: string; published_at: string
  before: RefreshNumbers | null; after: RefreshNumbers | null; status: 'too_early' | 'done'
}
/** The newest finished refresh whose draft is still open, linked above the cards. */
export type LatestRefresh = { draft_id: number; title: string | null; finished_at: string | null }
export type RefreshPages = { on: boolean; cards: RefreshCard[]; computed_at: string | null; refreshed: RefreshResult[]; latest?: LatestRefresh | null }
export const getRefreshPages = (slug: string) => api.get<RefreshPages>(`${ED}/ideas/refresh-pages?${qs({ publication_slug: slug })}`)
/** 202 started. 409 codes: edit_open, edit_off, already_running, switched_off, refreshed_recently (errorCode). */
export const refreshPage = (slug: string, articleId: number) =>
  api.post<{ started?: boolean }>(`${ED}/ideas/refresh-page?${qs({ publication_slug: slug })}`, { article_id: articleId })
/** Tick (or un-tick) one web fact. 409 not_open. */
export const setWebCheck = (id: number, key: string, checked: boolean) =>
  api.post<{ web_passages: WebPassage[] }>(`${ED}/pieces/${id}/web-checks`, { key, checked })
export type LiveChanges = {
  title: [string | null, string | null]; seo_title: [string | null, string | null]; meta_description: [string | null, string | null]
  ops: { op: 'equal' | 'insert' | 'delete'; text: string }[]
}
/** An edit draft against its live page. 409 stale_article / not_edit. */
export const getLiveChanges = (id: number) => api.get<LiveChanges>(`${ED}/pieces/${id}/live-changes`)
